'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PricingTier {
  duration: string;
  nights: number;
  discount: number;
}

interface PricingCalculatorProps {
  basePrice: number;
  apartmentName: string;
}

export default function PricingCalculator({ basePrice, apartmentName }: PricingCalculatorProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [nights, setNights] = useState(1);
  const [selectedTier, setSelectedTier] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const pricingTiers: PricingTier[] = [
    { duration: '1-6 Nights', nights: 1, discount: 0 },
    { duration: '7-13 Nights', nights: 7, discount: 5 },
    { duration: '14-29 Nights', nights: 14, discount: 10 },
    { duration: '30+ Nights', nights: 30, discount: 15 },
  ];

  const calculateTotal = () => {
    if (!isHydrated) return basePrice;
    const discount = pricingTiers[selectedTier].discount;
    const discountedPrice = basePrice - (basePrice * discount) / 100;
    return discountedPrice * nights;
  };

  const calculateSavings = () => {
    if (!isHydrated) return 0;
    const discount = pricingTiers[selectedTier].discount;
    return (basePrice * nights * discount) / 100;
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-2xl shadow-subtle border border-border p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-muted rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl shadow-subtle border border-border overflow-hidden">
      <div className="bg-gradient-to-r from-primary to-secondary p-6">
        <h3 className="font-display text-2xl font-bold text-white mb-2">Pricing Calculator</h3>
        <p className="text-white/80">{apartmentName}</p>
      </div>

      <div className="p-6">
        <div className="mb-6">
          <label className="block text-sm font-semibold text-text-primary mb-3">
            Select Stay Duration
          </label>
          <div className="grid grid-cols-2 gap-3">
            {pricingTiers.map((tier, index) => (
              <button
                key={index}
                onClick={() => {
                  setSelectedTier(index);
                  setNights(tier.nights);
                }}
                className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                  selectedTier === index
                    ? 'border-accent bg-accent/10 shadow-subtle'
                    : 'border-border hover:border-accent/50'
                }`}
              >
                <p className="font-semibold text-primary mb-1">{tier.duration}</p>
                {tier.discount > 0 && (
                  <p className="text-sm text-accent font-semibold">Save {tier.discount}%</p>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="nights-input" className="block text-sm font-semibold text-text-primary mb-3">
            Number of Nights: {nights}
          </label>
          <input
            id="nights-input"
            type="range"
            min="1"
            max="90"
            value={nights}
            onChange={(e) => setNights(parseInt(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-accent"
          />
          <div className="flex justify-between text-xs text-text-secondary mt-2">
            <span>1 night</span>
            <span>90 nights</span>
          </div>
        </div>

        <div className="bg-muted rounded-xl p-6 space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Base Rate (per night)</span>
            <span className="font-semibold text-primary">₹{basePrice.toLocaleString('en-IN')}</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Number of Nights</span>
            <span className="font-semibold text-primary">{nights}</span>
          </div>

          {pricingTiers[selectedTier].discount > 0 && (
            <div className="flex justify-between items-center text-success">
              <span className="flex items-center">
                <Icon name="TagIcon" size={18} className="mr-2" />
                Discount ({pricingTiers[selectedTier].discount}%)
              </span>
              <span className="font-semibold">-₹{calculateSavings().toLocaleString('en-IN')}</span>
            </div>
          )}

          <div className="border-t border-border pt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-primary">Total Amount</span>
              <span className="text-2xl font-bold text-accent">
                ₹{calculateTotal().toLocaleString('en-IN')}
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={() => window.open('https://bookingengine.stayflexi.com/?hotel_id=33526', '_blank')}
          className="w-full mt-6 px-6 py-4 bg-accent text-white font-semibold rounded-lg hover:bg-accent/90 shadow-subtle hover:shadow-elevated transition-all duration-300 flex items-center justify-center space-x-2">
          <Icon name="CalendarIcon" size={20} />
          <span>Check Availability</span>
        </button>

        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <p className="text-xs text-text-secondary text-center">
            * Prices are indicative and subject to availability. Additional taxes may apply.
          </p>
        </div>
      </div>
    </div>
  );
}